import {getBaseStickyHeaderHeight} from '@github-ui/sticky-css'
import {useCallback, useRef, useSyncExternalStore} from 'react'

function useElementPosition(element: HTMLElement | null) {
  const sizesCacheRef = useRef<number | null>(null)

  const subscribeToResize = useCallback(
    (notify: () => void) => {
      if (!element) return () => undefined

      // Perform initial read and cache it immediately on subscription
      sizesCacheRef.current = element.getBoundingClientRect().bottom

      const observer = new ResizeObserver(() => {
        // Update cache when ResizeObserver fires, batched by browser
        sizesCacheRef.current = element.getBoundingClientRect().bottom
        notify()
      })
      observer.observe(element)
      return () => {
        observer.unobserve(element)
        observer.disconnect()
      }
    },
    [element],
  )

  // getSnapshot reads from cache, but needs to handle initial render before subscription
  const getSnapshot = useCallback(() => {
    if (!element) return 0
    // On first call before subscription, read and cache the value
    if (sizesCacheRef.current === null) {
      sizesCacheRef.current = element.getBoundingClientRect().bottom
    }
    return sizesCacheRef.current
  }, [element])

  return useSyncExternalStore(subscribeToResize, getSnapshot)
}

// Wrapper function that takes a CSS selector and uses useElementPosition
export function useSelectorBottomPosition(selector: string) {
  const element = document.querySelector<HTMLElement>(selector)
  return useElementPosition(element)
}

export const useSiteHeaderHeight = () => {
  const appHeaderBottom = useSelectorBottomPosition('.AppHeader')
  const stickyHeaderBottom = useSelectorBottomPosition('.primary-viewer #issue-viewer-sticky-header')
  const notificationShelfHeight = getBaseStickyHeaderHeight()

  return appHeaderBottom > stickyHeaderBottom ? appHeaderBottom + notificationShelfHeight : stickyHeaderBottom
}
